import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ChevronDownIcon,
  GlobeAltIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

const ModernLanguageSwitcher: React.FC = () => {
  const { currentLanguage, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    {
      code: 'en',
      name: 'English',
      flag: '🇺🇸',
      native: 'English'
    },
    {
      code: 'ar',
      name: 'Arabic',
      flag: '🇪🇬',
      native: 'العربية'
    }
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];
  const buttonLabel = useMemo(() => {
    // Show the target language to switch to
    return currentLanguage === 'ar' ? 'English' : 'العربية';
  }, [currentLanguage]);

  const handleLanguageChange = (langCode: string) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      {/* Language Switcher Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 group"
        title={currentLanguage === 'ar' ? 'اللغة الحالية' : 'Current language'}
      >
        {/* Flag */}
        <span className="text-lg">{currentLang.flag}</span>
        
        {/* Language Code */}
        <span className="text-sm font-medium hidden sm:block">
          {buttonLabel}
        </span>
        
        {/* Dropdown Icon */}
        <ChevronDownIcon 
          className={`h-4 w-4 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-20 overflow-hidden">
            <div className="py-1">
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-primary-50 transition-colors duration-200 ${
                    currentLanguage === language.code 
                      ? 'bg-primary-50 text-primary-700' 
                      : 'text-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {/* Flag */}
                    <span className="text-lg">{language.flag}</span>
                    
                    {/* Language Info */}
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {language.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {language.native}
                      </span>
                    </div>
                  </div>
                  
                  {/* Check Icon */}
                  {currentLanguage === language.code && (
                    <CheckIcon className="h-4 w-4 text-primary-600" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ModernLanguageSwitcher;
